import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import _ from 'lodash';
import classnames from 'classnames';
import { useDispatch } from 'react-redux';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import { DnDContainer } from 'components/dragndrop/DnDContainer';
import {
  trophicLevels as baseTrophicLevels,
  pseudoTrophicLayerName,
} from 'constants/nodes';
import ButtonNew from './inputs/ButtonNew';
import TrophicLevel from './TrophicLevel';
import { update as updateMatrix } from 'actions/matrixSettings';
import { saveMatrix } from 'actions/matrices';
import { startEdit, finishEdit } from 'actions/matrixSettings';
import TextField from 'components/inputs/TextField';
import './TrophicLevels.scss';
import icons from 'img/icons';

const TrophicLevels = ({
  matrix,
  viewMode,
  currentNodes,
  nodeOverrides,
  nodesToOverride,
  setNodesToOverride,
  trophicCascadeLevel,
}) => {
  const dispatch = useDispatch();

  let [isEditing, setIsEditing] = useState(false);

  const isPlayMode = useMemo(() => {
    return viewMode === 'play';
  }, [viewMode]);

  // Save matrix
  const onSave = useCallback(
    nextMatrix => {
      nextMatrix.savedByUser = true;
      dispatch(finishEdit(nextMatrix));
      dispatch(saveMatrix(nextMatrix.id));
    },
    [dispatch]
  );

  // Delete trophic level
  const onDelete = useCallback(
    trophicLevelName => {
      let nextTrophicLevels = matrix?.trophicLevels
        ? JSON.parse(JSON.stringify(matrix.trophicLevels))
        : baseTrophicLevels;

      nextTrophicLevels = nextTrophicLevels.filter(
        t => t.name !== trophicLevelName
      );
      dispatch(startEdit(matrix));
      const nextMatrix = {
        ...matrix,
        trophicLevels: [...nextTrophicLevels],
      };
      dispatch(updateMatrix(nextMatrix));
      onSave(nextMatrix);
    },
    [dispatch, matrix, onSave]
  );

  // All trophic levels
  const trophicLevels = useMemo(() => {
    let nextLevels = matrix?.trophicLevels ?? baseTrophicLevels;
    if (matrix?.nodes) {
      nextLevels = nextLevels.map(l => {
        const isUsed = Object.values(matrix.nodes).some(
          n => n.trophicLevel === l.name
        );
        const isBaseLevel = baseTrophicLevels.some(v => v.name === l.name);
        return {
          ...l,
          canDelete: !isUsed && !isBaseLevel,
          draggable: !isBaseLevel && isEditing,
        };
      });
    }
    const hasUnnamedTrophicLevel =
      Object.values(matrix?.nodes).some(
        n => n.trophicLevel === pseudoTrophicLayerName
      ) || !Object.values(matrix?.nodes)?.length;
    return hasUnnamedTrophicLevel
      ? [
          {
            canDelete: false,
            name: pseudoTrophicLayerName,
            draggable: false,
            pseudoLevel: true,
          },
        ].concat(nextLevels)
      : nextLevels;
  }, [matrix, isEditing]);

  // Cards with trophic levels
  const [cards, setCards] = useState(null);
  const [committedCards, setCommittedCards] = useState(null);
  // Cards ref since onDrop doesn't get updated the way its passed to Card
  const cardsRef = useRef(cards);

  // Set ref since we can't get updates in onDrop
  useEffect(() => {
    cardsRef.current = cards;
  }, [cards]);

  // On drop, set a state we can use to save the new matrix
  const onDrop = useCallback(() => {
    const nextTrophicLevels = cardsRef.current.map(c => c.trophicLevel);
    // Confirm reorder
    if (matrix?.trophicLevels?.length !== nextTrophicLevels?.length) {
      return;
    }
    setCommittedCards(cardsRef.current);
  }, [matrix]);

  const updateTrophicLevels = useCallback(
    nextTrophicLevels => {
      // For proper function, we still need to start, update, and finish editing matrix
      // Even though we're passing a new matrix directly
      dispatch(startEdit(matrix));
      const nextMatrix = {
        ...matrix,
        trophicLevels: nextTrophicLevels,
      };
      dispatch(updateMatrix(nextMatrix));
      onSave(nextMatrix);
    },
    [dispatch, matrix, onSave]
  );

  const updateTrophicLevelsRef = useRef(updateTrophicLevels);

  useEffect(() => {
    updateTrophicLevelsRef.current = updateTrophicLevels;
  }, [updateTrophicLevels]);

  useEffect(() => {
    // Only updated on drop
    if (committedCards && committedCards.length) {
      const nextTrophicLevels = committedCards.map(c => c.trophicLevel);
      updateTrophicLevelsRef.current(nextTrophicLevels);
    }
  }, [committedCards]);

  // Create cards
  useEffect(() => {
    if (trophicLevels) {
      setCards(
        trophicLevels
          .filter(tl => {
            return isEditing
              ? true
              : Object.values(matrix?.nodes).some(
                  n => n.trophicLevel === tl?.name
                ) || tl?.name === pseudoTrophicLayerName;
          })
          .map((level, i) => {
            return {
              id: level?.name,
              draggable: level?.draggable,
              trophicLevel: level,
              component: (
                <TrophicLevel
                  key={i}
                  matrix={matrix}
                  viewMode={viewMode}
                  currentNodes={currentNodes}
                  nodeOverrides={nodeOverrides}
                  nodesToOverride={nodesToOverride}
                  setNodesToOverride={setNodesToOverride}
                  trophicCascadeLevel={trophicCascadeLevel}
                  level={level?.name}
                  color={level?.color}
                  icon={level?.icon}
                  pseudoLevel={level?.pseudoLevel}
                  onDelete={onDelete}
                  canDelete={level?.canDelete}
                  draggable={level?.draggable}
                />
              ),
            };
          })
      );
    }
  }, [
    onDelete,
    trophicLevels,
    currentNodes,
    matrix,
    nodeOverrides,
    nodesToOverride,
    setNodesToOverride,
    trophicCascadeLevel,
    viewMode,
    isEditing,
  ]);

  // Add a new trophic level
  const [inputting, setInputting] = useState(false);
  const [tempName, setTempName] = useState('');
  const [validationMessage, setValidationMessage] = useState(null);

  const submitTrophicLevel = useCallback(
    nextTrophicLevelName => {
      dispatch(startEdit(matrix));
      const nextTrophicLevel = {
        name: nextTrophicLevelName,
        color: '#E2E9EB',
        icon: icons?.trophicDefault,
      };

      const nextTrophicLevels = matrix?.trophicLevels
        ? JSON.parse(JSON.stringify(matrix.trophicLevels))
        : baseTrophicLevels;

      const nextMatrix = {
        ...matrix,
        trophicLevels: [...nextTrophicLevels, nextTrophicLevel],
      };
      dispatch(updateMatrix(nextMatrix));
      onSave(nextMatrix);
    },
    [dispatch, matrix, onSave]
  );

  const onCancelAdd = useCallback(() => {
    setInputting(false);
    setTempName('');
  }, []);

  const onConfirmAdd = useCallback(() => {
    submitTrophicLevel(tempName);
    onCancelAdd();
  }, [onCancelAdd, tempName, submitTrophicLevel]);

  useEffect(() => {
    const used = trophicLevels.some(t => t.name === tempName);

    if (used) {
      setValidationMessage('A trophic level with that name already exists');
    } else {
      setValidationMessage(null);
    }
  }, [tempName, trophicLevels]);

  // ----------------------------------------------------------------------------------------------------

  const [preEditState, setPreEditState] = useState(null);

  const matrixRef = useRef(matrix);

  useEffect(() => {
    if (matrix) {
      matrixRef.current = matrix;
    }
  }, [matrix]);

  useEffect(() => {
    if (!!isEditing) {
      setPreEditState(matrixRef.current);
    }
  }, [isEditing]);

  const onCancelEdit = useCallback(() => {
    if (!preEditState) return;
    dispatch(startEdit(matrix));
    dispatch(updateMatrix(preEditState));
    onSave(preEditState);
    setPreEditState(null);
    setIsEditing(false);
    onCancelAdd();
  }, [dispatch, matrix, preEditState, onSave, onCancelAdd]);

  const onConfirmEdit = useCallback(() => {
    setPreEditState(null);
    setIsEditing(false);
    onCancelAdd();
  }, [onCancelAdd]);

  return (
    <div className="TrophicLevels">
      <div
        className={classnames('TrophicLevels-main', { disabled: isEditing })}
      >
        {cards ? (
          <DndProvider backend={HTML5Backend}>
            <DnDContainer cards={cards} setCards={setCards} onDrop={onDrop} />
          </DndProvider>
        ) : null}
        {isEditing ? (
          <div className="button-container">
            {inputting ? (
              <div className="TrophicLevels-button-container">
                <TextField
                  onChange={setTempName}
                  placeholder={''}
                  validationMessage={validationMessage}
                  value={tempName}
                  className="SettingsTextField-input"
                />
                <ButtonNew
                  icon="check"
                  buttonClasses={classnames('icon-button', {
                    disabled: !tempName || !!validationMessage,
                  })}
                  onClick={onConfirmAdd}
                />
              </div>
            ) : (
              <ButtonNew
                buttonClasses="new-trophic-level-button"
                onClick={() => setInputting(true)}
                label="Add Trophic Level"
                iconLeft="plus"
              />
            )}
          </div>
        ) : null}
      </div>

      <div className="TrophicLevels-bottom-container">
        {!isPlayMode ? (
          <ButtonNew
            buttonClasses={classnames(
              'settings-action-button NodeSettings-edit-button',
              { active: isEditing }
            )}
            onClick={() => setIsEditing(!isEditing)}
            iconLeft="pencil"
            label={isEditing ? 'Editing Trophic Levels' : 'Edit Trophic Levels'}
          />
        ) : null}
        {isEditing ? (
          <div className="TrophicLevels-save-buttons">
            <ButtonNew
              buttonClasses={classnames('secondary-button-small')}
              onClick={onCancelEdit}
              label={'Cancel'}
            />
            <ButtonNew
              buttonClasses={classnames('primary-button-small')}
              onClick={onConfirmEdit}
              label={'Ok'}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default TrophicLevels;
